<template>
  <div>
    <div>
      <!-- Notes section (collapsible) -->
      <div class="w-full mb-4" v-if="(user.role == 'admin')">
        <div class="w-full px-4 py-3 cursor-pointer bg-primary-50 border border-primary-200 hover:bg-primary-100 transition-colors mt-3" :class="isNotesVisible ? 'rounded-t-lg border-b-0' : 'rounded-lg'" @click="isNotesVisible = !isNotesVisible">
          <div class="flex items-center justify-between">
            <div class="text-primary-700 font-bold text-base font-serif flex items-center">
              {{ $t('notes.notes_section') }}
              <span v-if="admin_notes" class="inline-block w-2.5 h-2.5 rounded-full bg-accent-400 ml-2"></span>
            </div>
            <div class="text-primary-500">
              <BaseIcon v-if="!isNotesVisible" icon="chevron-down" />
              <BaseIcon v-if="isNotesVisible" icon="chevron-up" />
            </div>
          </div>
        </div>
        <div v-if="isNotesVisible" class="px-4 pt-3 pb-4 bg-primary-50 border border-t-0 border-primary-200 rounded-b-lg">
          <textarea v-model="admin_notes" class="w-full h-36 p-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" :placeholder="$t('notes.admin_notes_placeholder')" :disabled="isDisabled"></textarea>
          <div class="flex items-center mt-2">
            <button class="btn-blue-outline mx-2" @click="toggleDisabled">
              {{ isDisabled ? $t('notes.unlock_notes') : $t('notes.lock_notes') }}
              <BaseIcon :icon="isDisabled ? 'lock' : 'lock-open'" class="ml-1"/>
            </button>
            <button class="btn-green-outline mx-2" v-if="(!this.isDisabled)" @click.prevent="saveAdminNotes()">
              {{ $t('notes.save') }}
              <BaseIcon icon="save" class="ml-1" />
            </button>
          </div>
        </div>
      </div>
      <!-- End Notes section -->
      <div class="flex">
        <div class="w-full bg-action-bar px-2 pt-0 pb-3 sm:pt-2 sm:pb-2 clearfix">
          <div class="float-left flx flex-wrap w-1/5 pt-1">
            <input v-model="searchQuery" type="text" :placeholder="$t('operations_page.unit_search')" class="w-full border border-gray-300 rounded py-1 mt-3 text-sm sm:mt-0 pl-3"/>
          </div>
          <div class="float-right flex flex-wrap pt-1">
            <button class="btn-secondary-outline mx-2 mt-3 sm:mt-0" @click.prevent="expandUnits()" v-if="filteredUnits && filteredUnits.length > 0">
              <span v-if="!all_expanded">
                {{ $t("operations_page.expand") }}
                <BaseIcon icon="plus-circle" class="ml-1" />
              </span>
              <span v-else>
                {{ $t("operations_page.minimize") }}
                <BaseIcon icon="minus-circle" class="ml-1" />
              </span>
            </button>
            <router-link class="btn-secondary-outline mx-2 mt-3 sm:mt-0" v-if="(user.role == 'admin' || user.role == 'seller' || (user.role == 'reseller' && user.reseller_special_access.indexOf('trip_report') > -1)) && filteredUnits && filteredUnits.length > 0" :to="'/trip-report/customer/' + customer_id">
              {{ $t("operations_page.trip_report") }}
              <BaseIcon icon="truck" class="ml-1" />
            </router-link>
            <router-link class="btn-secondary-outline mx-2 mt-3 sm:mt-0" v-if="(user.role == 'admin') && filteredUnits && filteredUnits.length > 0" :to="'/secure-route/customer/' + customer_id">
              {{ $t("secure_route") }}
              <BaseIcon icon="truck" class="ml-1" />
            </router-link>
            <router-link class="btn-secondary-outline mx-2 mt-3 sm:mt-0" v-if="(user.role == 'admin' || user.role == 'seller' || (user.role == 'reseller' && user.reseller_special_access.indexOf('securtrack') > -1) || ((user.role == 'superuser' || user.role == 'user') && hasAddon('gps_geofence'))) && filteredUnits && filteredUnits.length > 0" :to="'/gps-kund/' + customer_id">
              {{ $t("operations_page.securTrack") }}
              <BaseIcon icon="map-marker-alt" class="ml-1" />
            </router-link>
            <router-link class="btn-secondary-outline mx-2 mt-3 sm:mt-0" v-if="(user.role == 'admin' || user.role == 'seller' || (user.role == 'reseller' && user.reseller_special_access.indexOf('securtool') > -1) || ((user.role == 'superuser' || user.role == 'user') && hasAddon('rounding_surveillance'))) && filteredUnits && filteredUnits.length > 0" :to="'/ronderingsbevakning/customer/' + customer_id">
              {{ $t("operations_page.securTool") }}
              <BaseIcon icon="history" class="ml-1" />
            </router-link>
            <!-- SecurOne console: shown when the customer or one of its units has the addon; admin always gets in -->
            <router-link class="btn-secondary-outline mx-2 mt-3 sm:mt-0" v-if="securOneAvailable" :to="{ name: 'securone_overview', query: { org: customer_info._id } }">
              SecurOne
              <BaseIcon icon="shield-alt" class="ml-1" />
            </router-link>
            <router-link class="btn-secondary-outline mx-2 mt-3 sm:mt-0" v-if="(user.role == 'admin' || user.role == 'seller' || (user.role == 'reseller' && user.reseller_special_access.indexOf('test_reminder') > -1)) && filteredUnits && filteredUnits.length > 0" :to="'/alarm-reminder/customer/' + customer_id">
              {{ $t("operations_page.test_alarm") }}
              <BaseIcon icon="bell" class="ml-1" />
            </router-link>
            <button class="btn-red-outline mx-2 mt-3 sm:mt-0" v-if="(user.role == 'admin' || user.role == 'seller') && filteredUnits && filteredUnits.length > 0" @click.prevent="deleteUnitsPrompt()">
              {{ $t("operations_page.radera") }}
              <BaseIcon icon="trash" class="ml-1" />
            </button>
            <router-link v-if="(user.role == 'superuser' || user.role == 'user' || user.role == 'admin' || user.role == 'seller' || user.role == 'reseller') && customer_id != '-'" class="btn-green-outline mx-2 mt-3 sm:mt-0" :to="'/admin/skapa-verksamhet/' + customer_id">
              {{ $t("operations_page.new_business") }}
              <BaseIcon icon="plus" class="ml-1" />
            </router-link>
            <button class="btn-blue-outline mx-2 mt-3 sm:mt-0" @click.prevent="exportXLS()">
              {{ $t("operations_page.export") }}
              <BaseIcon icon="file-excel" class="ml-1" />
            </button>
          </div>
        </div>
      </div>
      <div v-if="filteredUnits && filteredUnits.length > 0" class="unit-wrapper">
        <div v-for="group in customerGroups" :key="group.customer_id">
        <div v-if="customer_id == '-'" class="font-serif text-base font-bold text-primary-700 mt-6 mb-1">{{ group.customer_name }}</div>
        <div class="grid bg-primary-50 rounded mt-5 py-5 px-4" v-for="unit in group.units" :key="unit._id">
          <div
            class="clearfix pb-2"
            v-bind:class="{ 'cursor-pointer border-b border-secondary-500': unit.alarms.length > 0 }"
            @click.prevent="
              unit.isAlarmVisible = !unit.isAlarmVisible;
              checkExpandUnit();
            "
          >
            <div class="float-left unit-desc">
              <span class="checkbox-wrapper mr-4">
                <input type="checkbox" class="export-checkbox" :value="unit._id" v-model="exportUnits" v-on:click.stop="" />
              </span>
              <span v-on:click.stop="$router.push('/larm/' + type + '/' + group.customer_id + '/' + unit.unit_id + '/' + id)" class="cursor-pointer">
                <span class="font-serif text-sm font-semibold mr-1 hover:text-primary-500 transition duration-200 underline" v-bind:class="{ active: unit.isAlarmVisible }">{{ unit.name }}</span>
                <span class="text-xs text-gray-400 hover:text-gray-500 transition duration-200">- {{ unit.company_number }}</span>
                <span v-if="/^admin|^seller/.test(user.role)" class="text-xxs text-gray-500 ml-8">{{ getSellerName(unit.seller_id) }}</span>
              </span>
              <!-- SecurOne per verksamhet: only a unit carrying the add-on itself is a SecurOne department (V11) -->
              <router-link v-if="securOneAvailable && hasSecurOneAddon(unit)" v-on:click.native.stop class="text-xxs text-primary-500 hover:underline ml-3" :to="{ name: 'securone_overview', query: { org: customer_info._id, unit: unit._id } }"><BaseIcon icon="shield-alt" class="mr-1" />SecurOne</router-link>
            </div>
            <div v-if="unit.alarms.length > 0" class="float-right">
              <BaseIcon v-if="!unit.isAlarmVisible" class="dropdown-icon text-primary-500" icon="plus-circle" />
              <BaseIcon v-if="unit.isAlarmVisible" class="dropdown-icon text-primary-500" icon="minus-circle" />
            </div>
          </div>
          <div class="alarm-item mt-3 border-b border-secondary-400 pt-1 pb-3" v-show="unit.isAlarmVisible" v-for="(alarm, c) in unit.alarms" :key="c">
            <router-link tag="span" class="alarm-name text-sm font-semibold cursor-pointer hover:text-primary-500 transition duration-200" :to="'/larm/' + type + '/' + group.customer_id + '/' + unit.unit_id + '/' + id + '/' + alarm._id">
              <span v-if="!/(SRT334)|(TWIG Beacon)|(4G Box)|(Anchor Beacon)|(AJAX)/.test(alarm.type) && checkAlarmGPS(alarm)" class="last-seen mr-2 text-sm" v-bind:class="{ inactive: getAlarmGPSTime(alarm.imei_number) > 1000 * 60 * 60 }">⬤</span>
              <span v-if="/(SRT334)|(TWIG Beacon)|(Anchor Beacon)|(AJAX)/.test(alarm.type)">
                <span v-if="alarm.last_rftag_activity" class="last-seen mr-2 text-sm" v-bind:class="{ inactive: getTagLastSeenDays(alarm) > 56, orange: getTagLastSeenDays(alarm) > 28 && getTagLastSeenDays(alarm) <= 56 }">⬤</span>
                <span v-else class="last-seen grey mr-2 text-sm">⬤</span>
              </span>
              <span v-if="/(4G Box)/.test(alarm.type)">
                <span v-if="alarm.last_rftag_activity" class="last-seen mr-2 text-sm" v-bind:class="{ inactive: getTagLastSeenDays(alarm) > 7, orange: getTagLastSeenDays(alarm) > 2 && getTagLastSeenDays(alarm) <= 7 }">⬤</span>
                <span v-else class="last-seen grey mr-2 text-sm">⬤</span>
              </span>
              <span>{{ alarm.unit_name }}</span>
              <span class="text-xs text-gray-400 hover:text-gray-500 transition duration-200 ml-3 font-light">- {{ alarm.phone }}</span>
              <span v-if="alarm.type" class="text-xs text-gray-400 hover:text-gray-500 transition duration-200 ml-2 font-light">({{ alarm.type }})</span>
            </router-link>
            <span class="float-right" v-if="alarm.type != 'SRT334' && checkAddons(unit)">
              <router-link class="btn-green" :to="'/gps-unit/' + type + '/' + unit.unit_id + '/' + id + '/' + alarm._id">
                {{ $t("operations_page.track") }}
                <BaseIcon icon="map-marker-alt" class="ml-2" />
              </router-link>
            </span>
          </div>
        </div>
        </div>
      </div>
      <div class="py-8 text-center border-b" v-if="filteredUnits && filteredUnits.length == 0">
        {{ $t("operations_page.no_business_found") }}
      </div>
    </div>
  </div>
</template>

<script>
import { isVirtualDevice } from "@/utils/virtualDevice";

export default {
  title() {
    return this.$t("page_titles.departments");
  },
  data() {
    return {
      type: this.$route.params.type,
      customer_id: this.$route.params.customer_id,
      id: this.$route.params.id,
      customer_info: null,
      customer_units: null,
      customers_list: [],
      units: null,
      reseller: null,
      all_expanded: false,
      exportUnits: [],
      gpsAlarms: [],
      isDisabled: true,
      isNotesVisible: false,
      admin_notes: "", 
      searchQuery: "",
    };
  },

  computed: {
    // SecurOne is opened per customer. Customer logins and organisation users
    // need the addon on the customer or on one of its units; a Securdia admin
    // may open it for any customer.
    securOneAvailable() {
      if (!this.customer_info || !this.customer_info._id || this.customer_id === '-') return false;
      if (!/^(admin|superuser|user)$/.test(this.user.role)) return false; // the reseller is not in this feature (AR-S2)
      // C1: no add-on, no entry - also for a Securdia admin
      return this.hasSecurOneAddon(this.customer_info) || (this.customer_units || []).some(this.hasSecurOneAddon);
    },
    user() {
      return this.$store.state.user;
    },
    filteredUnits() {
      if (!this.searchQuery) {
        return this.customer_units;
      }
      return this.customer_units.filter((unit) =>
        unit.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    // Units grouped under their parent customer. For the single-customer route
    // it's one group (header hidden); for the multi-customer aggregate view
    // (customer_id === '-', role 'user') units are grouped per customer.
    customerGroups() {
      let units = this.filteredUnits || [];
      if (this.customer_id !== "-") {
        return [{
          customer_id: this.customer_id,
          customer_name: this.customer_info ? this.customer_info.name : "",
          units: units,
        }];
      }
      let byParent = _.groupBy(units, (u) => String(u.parent_id));
      return _.map(byParent, (groupUnits, parentId) => {
        let c = _.find(this.customers_list, (x) => String(x._id) == parentId);
        return {
          customer_id: c ? c.customer_id : "-",
          customer_name: c ? c.name : "",
          units: groupUnits,
        };
      });
    },
  },

  methods: {
    hasSecurOneAddon(doc) {
      return Array.isArray(doc && doc.addons) && doc.addons.some((a) => String(a).toLowerCase() === 'securone');
    },
    getCustomer() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/customer/${this.customer_id}/units/alarms/`)
        .then((response) => {
          this.customer_info = response.data.customer;
          this.customers_list = response.data.customers || [];
          this.gpsAlarms = response.data.gps_alarms;
          this.admin_notes = response.data.customer ? response.data.customer.admin_notes : "";

          let title = this.customer_id === "-"
            ? this.$t("top_titles.departments")
            : `${this.customer_info ? this.customer_info.name : ""} - ${this.$t("top_titles.departments")}`;
          this.setPageTitle(title, "units");

          this.customer_units = [];
          // export
          this.exportUnits = [];

          response.data.units.forEach((el) => {
            el.isAlarmVisible = false;

            let productsSplit = _.partition(el.alarms, function(o) {
              return /(^SRT334$)|(^TWIG Beacon$)|(^4G Box$)|(Anchor Beacon)|(AJAX)/.test(o.type);
            });
            let alarms = [...productsSplit[1], ...productsSplit[0]];
            el.alarms = alarms;

            this.customer_units.push(el);
            // export
            this.exportUnits = [...this.exportUnits, el._id];
          });
        })
        .catch((error) => {
          this.handleError(error);
        });
    },
    checkAddons(unit){
      if (unit?.addons && unit.addons.indexOf('gps_geofence') >= 0) return true;
      if (this.user && (this.user.role === 'superuser' || this.user.role === 'user') && this.customer_info?.addons && this.customer_info.addons.indexOf('gps_geofence') >= 0) return true;
      return false;
    },
    hasAddon(addonName) {
      let addons = this.$store.state.addons;
      if (!addons) return false;
      if (this.user.role === 'superuser') {
        let customerAddons = addons.customers?.length > 0 ? _.flatten(_.map(addons.customers, 'addons')) : [];
        return customerAddons.indexOf(addonName) >= 0;
      } else if (this.user.role === 'user') {
        let unitAddons = addons.units?.length > 0 ? _.flatten(_.map(addons.units, 'addons')) : [];
        return unitAddons.indexOf(addonName) >= 0;
      }
      return false;
    },

    getResellerUnits() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/reseller/${this.id}/customer/${this.customer_id}/units`)
        .then((response) => {
          this.customer_info = response.data.customer;
          this.reseller = response.data.reseller;
          this.gpsAlarms = response.data.gps_alarms;

          this.setPageTitle(`${this.customer_info.name} - ${this.$t("top_titles.departments")}`, "units");

          this.customer_units = [];
          // export
          this.exportUnits = [];

          response.data.units.forEach((el) => {
            el.isAlarmVisible = false;

            let productsSplit = _.partition(el.alarms, function(o) {
              return /(^SRT334$)|(^TWIG Beacon$)|(Anchor Beacon)|(AJAX)/.test(o.type);
            });
            let alarms = [...productsSplit[1], ...productsSplit[0]];
            el.alarms = alarms;

            this.customer_units.push(el);
            // export
            this.exportUnits = [...this.exportUnits, el._id];
          });
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    getSellers() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/seller/reseller`)
        .then((response) => {
          this.sellers = response.data.data;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    getUnitName(unit_id) {
      // todo: replace unit_id with mongo _id
      let u = _.find(this.customer_units, { unit_id: unit_id });
      if (u) return u.name;
      else return "";
    },

    deleteUnitsPrompt() {
      if (this.exportUnits.length > 0) {
        this.$modal.show("dialog", {
          title: `<span class="text-red-500">${this.$t('modals.delete_units.title', { count: this.exportUnits.length })}</span>`,
          buttons: [
            {
              title: `<div class="bg-accent-500 text-white text-sm font-sans py-2">${this.$t('modals.cancel')}</div>`,
              handler: () => {
                this.$modal.hide("dialog");
              },
            },
            {
              title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t('modals.delete')}</div>`,
              handler: () => {
                this.deleteSelectedUnits();
              },
            },
          ],
        });
      }
    },

    async deleteSelectedUnits() {
      try {
        let data = {
          unitIDs: this.exportUnits,
        };

        // eslint-disable-next-line
        let response = await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/units/delete`, data);

        this.$modal.hide("dialog");
        this.handleSuccess(this.$t('modals.delete_units.success'));
        this.getCustomer();
      } catch (error) {
        this.$modal.hide("dialog");
        const safe4Text = this.safe4OffboardBlockedText(error);
        if (safe4Text) this.handleError(null, safe4Text, "top-right", 10000);
        else this.handleError(error);
      }
    },

    exportXLS() {
      let exportData = [];
      this.exportUnits.forEach((_id) => {
        let unit = _.find(this.customer_units, { _id: _id });
        // Include unit info with each alarm for export
        unit.alarms.forEach((alarm) => {
          // Hardware only. This report carries the billing columns per device,
          // and an app user's virtual device (user_id set) is a person's SOS
          // button that is never sold or invoiced (plan D2): listed, every app
          // user was a SecurApp/VIRT- line on the invoice basis, suspended
          // ones included. The page above still lists them; only the export
          // leaves them out.
          if (isVirtualDevice(alarm)) return;
          exportData.push({ ...alarm, unitInfo: unit });
        });
      });

      let export_columns = [];

      exportData.forEach((alarm) => {

        if (!this.gpsAlarmMap) {
          this.gpsAlarmMap = {};
          this.gpsAlarms.forEach(gps => {
            if (gps.imei_number) this.gpsAlarmMap[gps.imei_number] = gps;
          });
        }

        const gpsAlarm = this.gpsAlarmMap[alarm.imei_number];
        const lastUpdate = gpsAlarm?.updatedAt || "";
        const unitInfo = alarm.unitInfo || {};

        let reporting = this.$t("department_export.no");

        if (alarm.type && alarm.type.includes("SRT")) {
          let isRecent = lastUpdate && (new Date() - new Date(lastUpdate)) <= 60 * 60 * 1000;
          if ( isRecent ) reporting = this.$t("department_export.yes");
        }
        if (alarm.type && alarm.type.includes("TWIG")) {
          let isRecent = lastUpdate && (new Date() - new Date(lastUpdate)) <= 2.5 * 60 * 60 * 1000;
          if ( isRecent ) reporting = this.$t("department_export.yes");
        }

        const row = {
          [this.$t("department_export.customer_name")]: this.customerNameForUnit(unitInfo),
          [this.$t("department_export.department")]: this.getUnitName(alarm.parent_id),
          [this.$t("department_export.address")]: unitInfo.address || "",
          [this.$t("department_export.zip_code")]: unitInfo.zip_code || "",
          [this.$t("department_export.city")]: unitInfo.city || "",
          [this.$t("department_export.contact_name")]: unitInfo.contact_name || "",
          [this.$t("department_export.contact_email")]: unitInfo.contact_email || "",
          [this.$t("department_export.contact_phone")]: unitInfo.contact_phone || "",
          [this.$t("department_export.billing_address")]: unitInfo.billing_address || "",
          [this.$t("department_export.billing_zip_code")]: unitInfo.billing_zip_code || "",
          [this.$t("department_export.billing_city")]: unitInfo.billing_city || "",
          [this.$t("department_export.billing_reference")]: unitInfo.billing_reference || "",
          [this.$t("department_export.billing_invoice")]: unitInfo.billing_invoice || "",
          [this.$t("department_export.device_type")]: alarm.type,
          [this.$t("department_export.device_name")]: alarm.unit_name,
          [this.$t("department_export.phone")]: alarm.phone,
          [this.$t("department_export.serial_number")]: alarm.modelnumber,
          [this.$t("department_export.report")]: reporting,
          [this.$t("department_export.last_reported")]: lastUpdate ? this.moment(lastUpdate).format("YYYY-MM-DD HH:mm") : "",
        };
        if (this.user.role === "reseller") {
          row[this.$t("department_export.notes")] = alarm?.reseller_device_notes || "";
        }
        if (this.user.role === "admin") {
          row[this.$t("department_export.notes")] = alarm?.reseller_device_notes || "",
          row[this.$t("Admin anteckningar")] = alarm?.admin_device_notes || "";
        }
        export_columns.push(row);
      });

      export_columns = _.sortBy(export_columns, ["Verksamhet", "Enhetsnamn"]);

      this.excelExport(export_columns, "larms", `larm_rapport_${this.customer_info ? this.customer_info.name : "verksamheter"}`);
    },

    // Resolve the parent customer name for a unit (multi-customer aggregate
    // view groups units from several customers; single-customer view returns
    // the page customer).
    customerNameForUnit(unitInfo) {
      if (this.customer_id !== "-") return this.customer_info ? this.customer_info.name : "";
      let c = _.find(this.customers_list, (x) => String(x._id) == String(unitInfo.parent_id));
      return c ? c.name : "";
    },

    expandUnits() {
      this.all_expanded = !this.all_expanded;
      this.filteredUnits.forEach((unit) => {
        if (this.all_expanded) unit.isAlarmVisible = true;
        else unit.isAlarmVisible = false;
      });
    },

    checkExpandUnit() {
      let all_expanded = false;
      this.filteredUnits.forEach((unit) => {
        if (unit.isAlarmVisible && unit.alarms.length > 0) all_expanded = true;
      });

      if (all_expanded) this.all_expanded = true;
      else this.all_expanded = false;
    },

    checkAlarmGPS(alarm) {
      if (alarm.gps_active) {
        let a = _.find(this.gpsAlarms, { imei_number: alarm.imei_number });
        if (a) return true;
        else return false;
      } else return false;
    },

    getAlarmGPSTime(imei) {
      let a = _.find(this.gpsAlarms, { imei_number: imei });
      if (a) {
        return this.moment().diff(this.moment(a.updatedAt));
      }
    },

    getTagLastSeenDays(tag) {
      let tagLastSeen = this.moment().diff(this.moment(tag.last_rftag_activity), "days");
      return tagLastSeen;
    },

    checkGPSLogAddon() {
      if (this.$store.state.addons.findIndex((e) => e == "rounding_surveillance") >= 0) return true;
      else if (this.user.role == "admin") return true;
      else return false;
    },

    getSellerName(id) {
      let s = _.find(this.sellers, { _id: id });
      if (s) return s.name;
      else return "";
    },
    toggleDisabled() {
      this.isDisabled = !this.isDisabled;
    },
    async saveAdminNotes() {
      let data = {
          admin_notes: this.admin_notes
      };
      if (this.customer_id && data) {
        let response = await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/customer/admin-notes/${this.customer_id}`, data);
        if (!response) {
          this.handleError(response.message);
        } else {
          this.toggleDisabled()
        }
      } else return
    }
  },

  created() {
    if (this.type == "customer" && (this.user.role == "superuser" || this.user.role == "user" || this.user.role == "admin" || this.user.role == "seller")) this.getCustomer();
    else if (this.type == "reseller" && (this.user.role == "reseller" || this.user.role == "admin")) this.getResellerUnits();
    else this.customer_units = [];

    if (this.user.role == "admin") this.getSellers();
  },
  watch: {
    "$i18n.locale"() {
      document.title = this.$t("page_titles.departments");
      let title = this.customer_id === "-"
        ? this.$t("top_titles.departments")
        : `${this.customer_info ? this.customer_info.name : ""} - ${this.$t("top_titles.departments")}`;
      this.setPageTitle(title, "units");
    },
  },
};
</script>
